// Scene-reel replacement for VideoControlsStrip. The reel's length and cut are
// fixed by the workflow, so the only per-run choice is the clip model; the
// director switch and the camera-prompt fields do not apply (the backend
// refuses them with SCENE_REEL_FIELD_UNSUPPORTED rather than ignoring them, and
// the scene-reel request body never carries them).
//
// Copy rules: see WorkflowPicker.tsx — no "size", no "master".

import { Box, HStack, Select, Text } from '@chakra-ui/react';
import type { WizardSelections } from './index';
import type { WorkflowEntry } from './formatCatalog';
import { cutLabel } from '../../lib/sceneReel';

export function SceneReelControls({
  selections,
  workflow,
  onChange
}: {
  selections: WizardSelections;
  workflow:   WorkflowEntry;
  onChange:   (patch: Partial<WizardSelections>) => void;
}) {
  const current = workflow.models.find(m => m.key === selections.sceneModel && m.available) || null;
  const maxProducts = workflow.maxProducts;
  return (
    <Box borderWidth="1px" borderColor="brand.border" borderRadius="lg" bg="brand.surface" p={4}>
      <HStack spacing={6} align="flex-start" wrap="wrap">
        <Box>
          <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
            Scene model
          </Text>
          <Select
            size="sm"
            maxW="240px"
            value={current?.key ?? ''}
            onChange={(e) => onChange({ sceneModel: e.target.value || null })}
            aria-label="Scene model"
          >
            {!current && <option value="">Choose a model</option>}
            {workflow.models.map(m => (
              <option key={m.key} value={m.key} disabled={!m.available}>
                {m.label}{m.available ? '' : ' (not available)'}
              </option>
            ))}
          </Select>
          <Text fontSize="xs" color="brand.muted" mt={1} maxW="280px">
            Every scene in the reel is generated on this model.
          </Text>
        </Box>

        <Box>
          <Text fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted" mb={2}>
            Length
          </Text>
          <Text fontSize="sm" color="brand.ink" fontWeight="600">
            {workflow.durationSec} s · {cutLabel(workflow.cut)} · straight cuts
          </Text>
          <Text fontSize="xs" color="brand.muted" mt={1} maxW="280px">
            Fixed for scene reels. Up to {maxProducts} product{maxProducts === 1 ? '' : 's'} per run.
          </Text>
        </Box>
      </HStack>
      <Text fontSize="xs" color="brand.muted" mt={3}>
        AI director variants and prompt guidance don&apos;t apply to scene reels — each scene is planned
        from the product&apos;s own images. Pick up to {workflow.maxSeedPicksPerProduct} preferred scene
        images per product on the Products step, or leave them empty and the reel chooses.
      </Text>
    </Box>
  );
}
